import type { ApiFetchInit } from './fetch';
import { apiFetch } from './fetch';

const DEFAULT_LIST_TIMEOUT_MS = 15_000;

function mergeAbortSignal(
  parent: AbortSignal | null | undefined,
  timeoutMs: number,
): { signal: AbortSignal; dispose: () => void } {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  const onParentAbort = () => controller.abort();
  if (parent) {
    if (parent.aborted) controller.abort();
    else parent.addEventListener('abort', onParentAbort, { once: true });
  }

  return {
    signal: controller.signal,
    dispose: () => {
      clearTimeout(timer);
      if (parent) parent.removeEventListener('abort', onParentAbort);
    },
  };
}

export async function apiFetchWithTimeout(
  path: string,
  init?: ApiFetchInit,
  timeoutMs = DEFAULT_LIST_TIMEOUT_MS,
): Promise<Response> {
  const { signal, dispose } = mergeAbortSignal(init?.signal ?? undefined, timeoutMs);
  try {
    // This wrapper owns the stricter list timeout. Disable apiFetch's broader
    // per-attempt timeout so a custom value (for example 60 s media views) wins.
    return await apiFetch(path, { ...init, signal, timeoutMs: false });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error(`request timeout (${timeoutMs}ms): ${path}`);
    }
    throw err;
  } finally {
    dispose();
  }
}

export { DEFAULT_LIST_TIMEOUT_MS };
